import { useEffect, useState } from "react";
import { UserCog, UserPlus, KeyRound, Trash2, Shield, X, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function TeamTab() {
  const auth = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [perms, setPerms] = useState({ permissions: {}, roles: {} });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState(null);

  const load = () => {
    Promise.all([
      auth.apiGet("/admin/admins"),
      auth.apiGet("/admin/permissions"),
    ]).then(([a, p]) => { setAdmins(a.data); setPerms(p.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Sin permiso"));
  };
  useEffect(load, [auth]); // eslint-disable-line

  const resetPw = async (a) => {
    if (!window.confirm(`Generar nueva contraseña para ${a.email}?`)) return;
    try {
      const { data } = await auth.apiPost(`/admin/admins/${encodeURIComponent(a.email)}/reset-password`, {});
      setCreds({ email: a.email, password: data.new_password, mode: "reset" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Eliminar al admin ${a.email}? Esta acción no se puede deshacer.`)) return;
    try { await auth.apiDelete(`/admin/admins/${encodeURIComponent(a.email)}`); toast.success("Admin eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleActive = async (a) => {
    try {
      await auth.apiPatch(`/admin/admins/${encodeURIComponent(a.email)}`, { is_active: !a.is_active });
      toast.success(a.is_active ? "Admin desactivado" : "Admin reactivado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="tab-team" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Equipo administrativo · {admins.length} admin{admins.length !== 1 ? "es" : ""}</p>
        <button data-testid="team-new-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition">
          <UserPlus className="w-3.5 h-3.5" /> Nuevo admin
        </button>
      </div>

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5 divide-y">
        {admins.map((a) => (
          <div key={a.email} data-testid={`team-row-${a.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 items-center">
            <div className="col-span-12 md:col-span-4">
              <p className="font-medium text-sm">{a.name}</p>
              <p className="text-[11px] text-ink-mid font-mono">{a.email}</p>
            </div>
            <div className="col-span-6 md:col-span-2">
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-coral/10 text-coral">
                {a.admin_role_label}
              </span>
            </div>
            <div className="col-span-6 md:col-span-3 text-[11px] text-ink-mid">
              {a.effective_permissions.length} permisos efectivos
            </div>
            <div className="col-span-6 md:col-span-1 text-[11px]">
              {a.is_active ? <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-mono uppercase">Activo</span>
                            : <span className="px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-mono uppercase">Inactivo</span>}
            </div>
            <div className="col-span-6 md:col-span-2 flex justify-end gap-1">
              <IconBtn testid={`team-edit-${a.id}`}    icon={Pencil}   onClick={() => setEditing(a)} title="Editar" />
              <IconBtn testid={`team-resetpw-${a.id}`} icon={KeyRound} onClick={() => resetPw(a)}   title="Reset pw" />
              <IconBtn testid={`team-toggle-${a.id}`}  icon={Shield}   onClick={() => toggleActive(a)} title={a.is_active ? "Desactivar" : "Reactivar"} />
              <IconBtn testid={`team-delete-${a.id}`}  icon={Trash2}   onClick={() => remove(a)}     title="Eliminar"  className="text-red-700" />
            </div>
          </div>
        ))}
      </section>

      {creating && <CreateAdminModal perms={perms} onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); setCreds(c); load(); }} />}
      {editing && <EditAdminModal admin={editing} perms={perms} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creds && <CredsModal data={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, title, testid, className = "" }) {
  return <button data-testid={testid} onClick={onClick} title={title} className={`p-1.5 rounded-full hover:bg-cream-deep transition ${className}`}><Icon className="w-3.5 h-3.5" /></button>;
}

function CreateAdminModal({ perms, onClose, onCreated }) {
  const auth = useAdminAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("soporte");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await auth.apiPost("/admin/admins", { email, name, admin_role: role });
      onCreated({ email: data.admin.email, password: data.generated_password, mode: "create" });
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Nuevo admin" onClose={onClose} testid="team-create-modal">
      <form onSubmit={submit} className="space-y-3">
        <In testid="team-create-name" label="Nombre completo" value={name} onChange={setName} required />
        <In testid="team-create-email" label="Email" type="email" value={email} onChange={setEmail} required />
        <Sel testid="team-create-role" label="Rol" value={role} onChange={setRole}
             options={Object.entries(perms.roles || {}).map(([id, r]) => ({ id, label: r.label }))} />
        <div className="text-[11px] text-ink-mid bg-cream rounded-lg p-2 max-h-32 overflow-auto" data-testid="team-create-perms-preview">
          <p className="font-mono mb-1 text-coral">Permisos del rol:</p>
          {(perms.roles?.[role]?.permissions || []).map((p) => <p key={p} className="font-mono">• {perms.permissions?.[p] || p}</p>)}
        </div>
        <button type="submit" disabled={busy} data-testid="team-create-submit" className="w-full bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral-hover disabled:opacity-50">
          {busy ? "Creando…" : "Crear admin"}
        </button>
      </form>
    </Modal>
  );
}

function EditAdminModal({ admin, perms, onClose, onSaved }) {
  const auth = useAdminAuth();
  const [role, setRole] = useState(admin.admin_role);
  const [extra, setExtra] = useState(new Set(admin.extra_perms || []));
  const [denied, setDenied] = useState(new Set(admin.denied_perms || []));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await auth.apiPatch(`/admin/admins/${encodeURIComponent(admin.email)}`,
        { admin_role: role, extra_perms: Array.from(extra), denied_perms: Array.from(denied) });
      toast.success("Admin actualizado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const togglePerm = (p, kind) => {
    if (kind === "extra") {
      const s = new Set(extra); s.has(p) ? s.delete(p) : s.add(p); setExtra(s);
      const d = new Set(denied); d.delete(p); setDenied(d);
    } else {
      const s = new Set(denied); s.has(p) ? s.delete(p) : s.add(p); setDenied(s);
      const e2 = new Set(extra); e2.delete(p); setExtra(e2);
    }
  };

  const rolePerms = new Set(perms.roles?.[role]?.permissions || []);
  return (
    <Modal title={`Editar · ${admin.email}`} onClose={onClose} testid="team-edit-modal">
      <form onSubmit={submit} className="space-y-3">
        <Sel testid="team-edit-role" label="Rol base" value={role} onChange={setRole}
             options={Object.entries(perms.roles || {}).map(([id, r]) => ({ id, label: r.label }))} />
        <div className="border hairline bg-cream rounded-xl p-3 max-h-72 overflow-auto" data-testid="team-edit-perms">
          <p className="font-mono text-[10px] uppercase tracking-wider text-coral mb-2">Personalizar permisos</p>
          {Object.entries(perms.permissions || {}).map(([p, label]) => {
            const inRole = rolePerms.has(p);
            const isExtra = extra.has(p);
            const isDenied = denied.has(p);
            const effective = (inRole && !isDenied) || isExtra;
            return (
              <div key={p} className="flex items-start gap-2 py-1.5 text-xs border-b hairline last:border-b-0">
                <span className="flex-1">
                  <span className={effective ? "text-ink" : "text-ink-mid line-through"}>{label}</span>
                  <span className="block text-[10px] text-ink-mid font-mono">{p}{inRole ? " · base" : ""}</span>
                </span>
                <div className="flex gap-1 shrink-0">
                  {!inRole && (
                    <button type="button" onClick={() => togglePerm(p, "extra")} data-testid={`team-edit-extra-${p}`}
                      className={`text-[10px] px-2 py-0.5 rounded-full ${isExtra ? "bg-emerald-600 text-white" : "bg-cream-deep border hairline"}`}>+</button>
                  )}
                  {inRole && (
                    <button type="button" onClick={() => togglePerm(p, "denied")} data-testid={`team-edit-denied-${p}`}
                      className={`text-[10px] px-2 py-0.5 rounded-full ${isDenied ? "bg-red-600 text-white" : "bg-cream-deep border hairline"}`}>−</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button type="submit" disabled={busy} data-testid="team-edit-submit" className="w-full bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral-hover disabled:opacity-50">
          {busy ? "Guardando…" : "Guardar"}
        </button>
      </form>
    </Modal>
  );
}

function CredsModal({ data, onClose }) {
  const copy = () => { navigator.clipboard.writeText(`${data.email}\n${data.password}`); toast.success("Copiado"); };
  return (
    <Modal title={data.mode === "reset" ? "Contraseña restablecida" : "Admin creado"} onClose={onClose} testid="team-creds-modal">
      <div className="space-y-3">
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">⚠ Esta es la única vez que verás esta contraseña. Cópiala y compártela de forma segura.</div>
        <div className="bg-cream rounded-xl p-3 border hairline space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-wider text-ink-mid">Email</p>
          <p className="font-mono text-sm break-all">{data.email}</p>
          <p className="mt-2 text-[10px] font-mono uppercase tracking-wider text-ink-mid">Contraseña</p>
          <p data-testid="team-creds-pw" className="font-mono text-sm break-all">{data.password}</p>
        </div>
        <button onClick={copy} data-testid="team-creds-copy" className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-2.5 text-sm hover:bg-coral"><Copy className="w-3.5 h-3.5" /> Copiar</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div onClick={onClose} className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} data-testid={testid} className="bg-cream-card border hairline rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl tracking-tight">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-cream-deep flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function In({ label, testid, type = "text", value, onChange, ...rest }) {
  return <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span><input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral" /></label>;
}
function Sel({ label, testid, value, onChange, options }) {
  return <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span><select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral">{options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}</select></label>;
}
